import React from 'react';

interface TermsConditionsModelProps {
  onClose: () => void;
  onAccept: () => void;
  isLoading: boolean;
}

const TermsConditionsModel: React.FC<TermsConditionsModelProps> = ({ onClose, onAccept, isLoading }) => {
  return (
    <div className="fixed inset-0 z-[101] bg-black/0 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="relative w-[60vw] max-w-3xl h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-slideUp">

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b bg-gradient-to-r blue-50 to-blue-100">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Terms and Conditions</h2>
            <p className="text-sm text-gray-500 mt-1">Last Updated: April 21, 2026</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all hover:scale-105"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="max-w-3xl mx-auto text-gray-700 space-y-6">
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">1</span>
                Acceptance of Terms
              </h3>
              <p className="text-sm leading-7 pl-8">
                By accessing or using our services, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">2</span>
                Use of Service
              </h3>
              <p className="text-sm leading-7 pl-8">
                You must follow any policies made available to you within the Services. Don't misuse our Services. For example, don't interfere with our Services or try to access them using a method other than the interface and the instructions that we provide.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">3</span>
                Account Responsibilities
              </h3>
              <p className="text-sm leading-7 pl-8">
                You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">4</span>
                Privacy
              </h3>
              <p className="text-sm leading-7 pl-8">
                Your use of our services is also subject to our Privacy Policy. We collect and process your personal information in accordance with our privacy practices.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">5</span>
                Intellectual Property
              </h3>
              <p className="text-sm leading-7 pl-8">
                Our services and their content are owned by us and protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our permission.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">6</span>
                Limitation of Liability
              </h3>
              <p className="text-sm leading-7 pl-8">
                We are not liable for any indirect, incidental, special, or consequential damages arising from your use of the services. Our total liability shall not exceed the amount you paid us in the past twelve months.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">7</span>
                Changes to Terms
              </h3>
              <p className="text-sm leading-7 pl-8">
                We may modify these terms at any time. We will notify you of material changes. Continued use of the services constitutes acceptance of modified terms.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">8</span>
                Contact
              </h3>
              <p className="text-sm leading-7 pl-8">
                For questions about these terms, please contact us at <span className="text-blue-600">support@alonzoai.com</span>
              </p>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-white px-8 py-5 flex justify-center gap-4">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-full border-2 border-gray-300 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          {/* <button
            onClick={onAccept}
            disabled={isLoading}
            className="px-8 py-2.5 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-blue-200"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Accepting...
              </>
            ) : (
              'Accept Terms'
            )}
          </button> */}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TermsConditionsModel;